/**
 * Response Status Mapping table for the SFTP Push tab. Each row maps a condition
 * field + value(s) to an output code. When no field is selected the row is saved
 * as a `status_group` on `response_status`; otherwise it becomes a `special_case`
 * with clauses on the selected field.
 */
import {
  MultiSelectWithTag,
  SearchableSelect,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo } from "react";
import { Button, TextInput } from "risa-oasis-ui_v2";
import { useQueryBuilderValueSearch } from "../../../../../components/queryBuilder/useQueryBuilderValueSearch";
import { QueryBuilderConfig } from "../../../../../data-model/queryBuilder";
import { CmmInputUniqueValuesDoc } from "../../validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";
import { AddIcon } from "../../../../../svg/add-icon";
import { Delete } from "../../../../../svg/delete";
import { ResponseStatusRow } from "./nycbsSftpPushRows";
import {
  getConditionFieldSelectOptions,
  getFieldValueSelectOptions,
  RESPONSE_STATUS_FIELD,
} from "./responseStatusMappingOptions";

interface ResponseStatusMappingTableProps {
  conditionConfig: QueryBuilderConfig;
  cmmInputUniqueValues: CmmInputUniqueValuesDoc | null;
  rows: ResponseStatusRow[];
  onChange: (rows: ResponseStatusRow[]) => void;
  onAdd: () => void;
}

interface ResponseStatusMappingRowProps {
  row: ResponseStatusRow;
  conditionConfig: QueryBuilderConfig;
  cmmInputUniqueValues: CmmInputUniqueValuesDoc | null;
  onPatch: (id: string, patch: Partial<ResponseStatusRow>) => void;
  onRemove: (id: string) => void;
}

/**
 * Single editable row in the Response Status Mapping table.
 *
 * @param props - Row state, shared option sources, and change handlers
 * @return Row UI with condition field, value, and code inputs
 */
const ResponseStatusMappingRow = ({
  row,
  conditionConfig,
  cmmInputUniqueValues,
  onPatch,
  onRemove,
}: ResponseStatusMappingRowProps) => {
  const fieldOptions = useMemo(
    () =>
      getConditionFieldSelectOptions(
        conditionConfig,
        row.secondStp ? [row.secondStp] : [],
      ),
    [conditionConfig, row.secondStp],
  );

  const valueFieldName = row.secondStp.trim() || RESPONSE_STATUS_FIELD;

  const valueInitialOptions = useMemo(
    () =>
      getFieldValueSelectOptions(
        conditionConfig,
        cmmInputUniqueValues,
        valueFieldName,
        row.statuses,
      ),
    [conditionConfig, cmmInputUniqueValues, valueFieldName, row.statuses],
  );

  const valueSearchProps = useQueryBuilderValueSearch({
    columnName: valueFieldName,
    initialOptions: valueInitialOptions,
    resetDeps: [row.statuses, valueFieldName, conditionConfig, cmmInputUniqueValues],
    allowCustomValueOnEmptySearch: true,
  });

  return (
    <div className="flex flex-row items-center gap-2">
      <div className="w-48">
        <SearchableSelect
          id={`sftp-condition-field-${row.id}`}
          label=""
          options={fieldOptions}
          value={row.secondStp}
          defaultValue={row.secondStp}
          placeholder="—"
          onOptionChange={(data: { value: string }) =>
            onPatch(row.id, { secondStp: data.value, statuses: [] })
          }
        />
      </div>
      <div className="flex-1">
        <MultiSelectWithTag
          {...valueSearchProps}
          value={row.statuses}
          onChange={(values) => onPatch(row.id, { statuses: values })}
          placeholder="Select values..."
          dropdownWidth="14rem"
        />
      </div>
      <div className="w-28">
        <TextInput
          id={`sftp-code-${row.id}`}
          label=""
          hideLabel
          placeholder="Code"
          defaultValue={row.code}
          onChange={(data: { value?: string }) =>
            onPatch(row.id, { code: data.value ?? "" })
          }
          onBlur={(data: { value?: string }) =>
            onPatch(row.id, { code: data.value ?? "" })
          }
        />
      </div>
      <div
        className="flex w-6 cursor-pointer justify-center"
        onClick={() => onRemove(row.id)}
      >
        <Delete width={16} height={16} />
      </div>
    </div>
  );
};

interface ReadOnlyResponseStatusRowProps {
  row: ResponseStatusRow;
  onRemove: (id: string) => void;
}

/**
 * Read-only row for a special case authored outside this table — a multi-value
 * IN, a nested clause group, or several condition fields. Editing it here would
 * silently drop clauses, so the stored rule is shown as text and saved back
 * unchanged. Delete still works.
 *
 * @param props - Row state and the remove handler
 * @return Non-editable summary row
 */
const ReadOnlyResponseStatusRow = ({
  row,
  onRemove,
}: ReadOnlyResponseStatusRowProps) => (
  <div className="flex flex-row items-center gap-2">
    <div className="w-48 text-tiny text-primaryGray-6">
      Advanced rule (read-only)
    </div>
    <div
      className="flex-1 truncate rounded border border-primaryGray-16 px-3 py-2 text-tiny text-primaryGray-1"
      title={row.rawSummary}
    >
      {row.rawSummary}
    </div>
    <div className="w-28 px-1 text-tiny text-primaryGray-1">{row.code}</div>
    <div
      className="flex w-6 cursor-pointer justify-center"
      onClick={() => onRemove(row.id)}
    >
      <Delete width={16} height={16} />
    </div>
  </div>
);

const ResponseStatusMappingTable = ({
  conditionConfig,
  cmmInputUniqueValues,
  rows,
  onChange,
  onAdd,
}: ResponseStatusMappingTableProps) => {
  const patchRow = useMemo(
    () => (id: string, patch: Partial<ResponseStatusRow>) =>
      onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row))),
    [rows, onChange],
  );

  const removeRow = (id: string) =>
    onChange(rows.filter((row) => row.id !== id));

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primaryGray-16 p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-primaryGray-1">
          Response Status Mapping
        </div>
        <Button
          buttonType="primary"
          size="medium"
          disabled={false}
          onClick={onAdd}
        >
          <AddIcon width="14" height="14" color="#ffffff" />
          Add new column
        </Button>
      </div>

      <div className="flex flex-row gap-2 px-1 text-tiny font-semibold text-primaryGray-6">
        <div className="w-48">Field</div>
        <div className="flex-1">Value</div>
        <div className="w-28">Code</div>
        <div className="w-6" />
      </div>

      {rows.length === 0 ? (
        <div className="text-tiny text-primaryGray-12">No rows configured.</div>
      ) : (
        rows.map((row) =>
          row.raw ? (
            <ReadOnlyResponseStatusRow
              key={row.id}
              row={row}
              onRemove={removeRow}
            />
          ) : (
            <ResponseStatusMappingRow
              key={row.id}
              row={row}
              conditionConfig={conditionConfig}
              cmmInputUniqueValues={cmmInputUniqueValues}
              onPatch={patchRow}
              onRemove={removeRow}
            />
          ),
        )
      )}
    </div>
  );
};

export default ResponseStatusMappingTable;
